import { FC, SVGProps } from 'react'
import { INDUSTRIES } from './CreateProjectModal'

export interface ProjectAppearance {
  Icon: FC<SVGProps<SVGSVGElement>>
  accent: string
  background: string
}

export const BuildingIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path d="M7 20V4h10v16" stroke="currentColor" strokeLinejoin="round" />
    <path d="M5 20h14" stroke="currentColor" strokeLinecap="round" />
    <path
      d="M10 8h.01M14 8h.01M10 12h.01M14 12h.01M10 16h.01M14 16h.01"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2"
    />
  </svg>
)

export const HomeOutlineIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path
      d="M4.5 11.2 12 4.5l7.5 6.7V20H15v-5.5H9V20H4.5v-8.8Z"
      stroke="currentColor"
      strokeLinejoin="round"
    />
  </svg>
)

export const CoffeeIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg fill="none" viewBox="0 0 24 24" {...props}>
    <path
      d="M5 9h10v5.5a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V9Z"
      stroke="currentColor"
    />
    <path
      d="M15 10.5h1.8a2.2 2.2 0 0 1 0 4.4H15M7.5 5.5v1.2M10.5 5.5v1.2M13.5 5.5v1.2M4 20h14"
      stroke="currentColor"
      strokeLinecap="round"
    />
  </svg>
)

const PROJECT_APPEARANCES: Record<string, ProjectAppearance> = {
  'food-beverage': {
    Icon: CoffeeIcon,
    accent: '#9E4CD6',
    background: '#F0DDFC',
  },
  hospitality: {
    Icon: BuildingIcon,
    accent: '#8080FF',
    background: '#EEEEFF',
  },
  'real-estate': {
    Icon: HomeOutlineIcon,
    accent: '#13B66D',
    background: '#DDF8EA',
  },
}

const DEFAULT_PROJECT_APPEARANCE: ProjectAppearance = {
  Icon: BuildingIcon,
  accent: '#D99727',
  background: '#FFF0CF',
}

export const getProjectAppearance = (
  industries: string[] = []
): ProjectAppearance => {
  for (const industry of industries) {
    if (PROJECT_APPEARANCES[industry]) {
      return PROJECT_APPEARANCES[industry]
    }
  }

  return DEFAULT_PROJECT_APPEARANCE
}

// Looked up from INDUSTRIES — the same list the create modal's step 1 renders —
// so a project card reads exactly as the industry the user picked. Deriving the
// label from the id instead turned 'ict' into "Ict" and 'e-commerce' into
// "E Commerce", neither of which appears anywhere in the picker.
//
// The fallback still title-cases, so an id that predates the current list (or
// arrives from elsewhere) renders readably rather than raw.
const INDUSTRY_LABEL_BY_ID = new Map<string, string>(
  INDUSTRIES.map(({ id, label }) => [id, label])
)

const titleCase = (industry: string) =>
  industry
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

export const formatIndustries = (industries: string[] = []) =>
  industries
    .map(
      (industry) => INDUSTRY_LABEL_BY_ID.get(industry) || titleCase(industry)
    )
    .join(' · ')
